import React, { useState, useEffect } from 'react';
import { diligenceApi, dealsApi, documentsApi } from '../services/api';
import { 
  FileSearch, AlertTriangle, Clock, User, Calendar, 
  ChevronDown, ChevronRight, Download, FileText, 
  MessageSquare, CheckCircle, AlertCircle, Circle,
  X, Plus, Trash2, Pencil, Save, XCircle
} from 'lucide-react';
import './DiligenceTracker.css';



// Default category colors (fallback)
const DEFAULT_CATEGORY_COLORS = {
  'Financial': '#3b82f6',
  'Legal': '#8b5cf6',
  'Operational': '#f59e0b',
  'HR': '#ec4899',
  'IT': '#06b6d4',
  'Tax': '#10b981'
};

const STATUS_CONFIG = {
  Open: { 
    icon: Circle, 
    color: '#ef4444', 
    bgColor: '#fee2e2',
    label: 'Open'
  },
  Partial: { 
    icon: AlertCircle, 
    color: '#f59e0b', 
    bgColor: '#fef3c7',
    label: 'Partial'
  },
  Closed: { 
    icon: CheckCircle, 
    color: '#10b981', 
    bgColor: '#d1fae5',
    label: 'Closed'
  }
};

const CATEGORY_COLORS = {
  Financial: '#3b82f6',
  Legal: '#8b5cf6',
  Operational: '#f59e0b',
  HR: '#ec4899',
  IT: '#06b6d4',
  Tax: '#10b981'
};

function DiligenceTracker({ selectedDeal }) {
  const [deal, setDeal] = useState(selectedDeal);
  const [deals, setDeals] = useState([]);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [criticalGaps, setCriticalGaps] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [expandedItem, setExpandedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkingItem, setLinkingItem] = useState(null);
  
  // Editing state
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({ userTitle: '', userDescription: '' });
  
  // Add item state
  const [addingToCategory, setAddingToCategory] = useState(null);
  const [addItemForm, setAddItemForm] = useState({
    code: '',
    title: '',
    description: '',
    status: 'Open',
    owner: '',
    dueDate: ''
  });
  
  // Comment state
  const [commentText, setCommentText] = useState('');
  const [addingCommentTo, setAddingCommentTo] = useState(null);
  
  // Add category state
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [addCategoryForm, setAddCategoryForm] = useState({
    name: '',
    codePrefix: '',
    color: '#6b7280'
  });
  
  // Color options for categories
  const colorOptions = [
    '#3b82f6', '#8b5cf6', '#f59e0b', '#ec4899', '#06b6d4', '#10b981',
    '#ef4444', '#f97316', '#eab308', '#84cc16', '#14b8a6', '#6366f1',
    '#a855f7', '#d946ef', '#f43f5e', '#64748b'
  ];

  // Load deals on mount
  useEffect(() => {
    fetchDeals();
    fetchDocuments();
  }, []);

  // Load tracker when deal changes
  useEffect(() => {
    if (deal) {
      fetchDiligenceTracker();
    }
  }, [deal?.id]);

  const fetchDeals = async () => {
    try {
      const response = await dealsApi.list();
      setDeals(response.data || []);
      if (!deal && response.data?.length > 0) {
        setDeal(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching deals:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await documentsApi.list();
      setDocuments(response.data?.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const fetchDiligenceTracker = async () => {
    if (!deal) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const [trackerRes, categoriesRes] = await Promise.all([
        diligenceApi.getByDeal(deal.id),
        // Categories are included in the diligence response
      ]);
      
      const fetchedCategories = categoriesRes.data.categories || [];
      setCategories(fetchedCategories);
      setItems(trackerRes.data.items || []);
      setStats(trackerRes.data.stats || null);
      setCriticalGaps(trackerRes.data.criticalGaps || []);
      
      // Check for category filter from overview table
      const selectedCategory = localStorage.getItem('selectedDiligenceCategory');
      if (selectedCategory) {
        const categoryExists = fetchedCategories.find(c => c.name === selectedCategory);
        if (categoryExists) {
          setExpandedCategories([selectedCategory]);
        } else if (fetchedCategories.length > 0) {
          setExpandedCategories([fetchedCategories[0].name]);
        }
        localStorage.removeItem('selectedDiligenceCategory');
      } else if (expandedCategories.length === 0 && fetchedCategories.length > 0) {
        // Auto-expand first category if none expanded
        setExpandedCategories([fetchedCategories[0].name]);
      }
      
      // Show warning if no documents
      if (!trackerRes.data.hasDocuments) {
        setError('No documents uploaded. Tracker cannot evaluate coverage.');
      }
    } catch (error) {
      console.error('Error fetching diligence tracker:', error);
      setError('Failed to load information tracker');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const toggleItem = (itemId) => {
    setExpandedItem(expandedItem === itemId ? null : itemId);
    // Clear editing state when collapsing
    if (expandedItem === itemId) {
      setEditingItem(null);
    }
  };

  // Edit item handlers
  const startEditing = (item, e) => {
    e.stopPropagation();
    setEditingItem(item.id);
    setEditForm({
      userTitle: item.userTitle || item.title || '',
      userDescription: item.userDescription || item.description || ''
    });
  };

  const cancelEditing = (e) => {
    e?.stopPropagation();
    setEditingItem(null);
    setEditForm({ userTitle: '', userDescription: '' });
  };

  const saveItemEdits = async (item, e) => {
    e.stopPropagation();
    if (!deal) return;
    
    const originalItem = { ...item };
    
    // Optimistic update
    const updatedItems = items.map(i => {
      if (i.id === item.id) {
        return {
          ...i,
          userTitle: editForm.userTitle,
          userDescription: editForm.userDescription,
          updatedAt: new Date().toISOString()
        };
      }
      return i;
    });
    setItems(updatedItems);
    setEditingItem(null);
    
    try {
      await diligenceApi.update(item.id, {
        content: {
          userTitle: editForm.userTitle || null,
          userDescription: editForm.userDescription || null
        }
      });
      // Refresh to get server state
      fetchDiligenceTracker();
    } catch (error) {
      console.error('Error saving item edits:', error);
      // Rollback on error
      setItems(items.map(i => i.id === item.id ? originalItem : i));
      setError('Failed to save changes');
    }
  };

  // Handle status change
  const handleStatusChange = async (item, newStatus, e) => {
    e?.stopPropagation();
    if (!deal) return;
    
    const originalItem = { ...item };
    
    // Optimistic update
    const updatedItems = items.map(i => {
      if (i.id === item.id) {
        return { ...i, status: newStatus, manualStatus: newStatus };
      }
      return i;
    });
    setItems(updatedItems);
    
    try {
      await diligenceApi.updateStatus(item.id, {
        status: newStatus
      });
      // Refresh to get server state
      fetchDiligenceTracker();
    } catch (error) {
      console.error('Error updating status:', error.response?.data || error.message);
      // Rollback on error
      setItems(items.map(i => i.id === item.id ? originalItem : i));
      setError(error.response?.data?.error || error.response?.data?.message || 'Failed to update status');
    }
  };

  // Add item handlers
  const startAddingItem = (category, e) => {
    e.stopPropagation();
    setAddingToCategory(category);
    setAddItemForm({
      code: '',
      title: '',
      description: '',
      status: 'Open',
      owner: '',
      dueDate: ''
    });
  };

  const cancelAddingItem = () => {
    setAddingToCategory(null);
    setAddItemForm({
      code: '',
      title: '',
      description: '',
      status: 'Open',
      owner: '',
      dueDate: ''
    });
  };

  const saveNewItem = async () => {
    if (!deal || !addingToCategory) return;
    if (!addItemForm.title.trim()) return;
    
    try {
      const response = await diligenceApi.create({ 
        deal_id: deal.id,
        category: addingToCategory,
        code: addItemForm.code.trim() || undefined,
        title: addItemForm.title.trim(),
        description: addItemForm.description.trim(),
        status: addItemForm.status,
        owner: addItemForm.owner.trim() || null,
        dueDate: addItemForm.dueDate || null
      });
      
      // Add new item to list
      setItems(prev => [...prev, response.data.item]);
      setStats(response.data.stats);
      
      // Clear form
      cancelAddingItem();
      
      // Expand the category to show the new item
      if (!expandedCategories.includes(addingToCategory)) {
        setExpandedCategories(prev => [...prev, addingToCategory]);
      }
    } catch (error) {
      console.error('Error creating item:', error);
      setError('Failed to create item');
    }
  };

  // Comment handlers
  const addComment = async (item) => {
    if (!deal || !commentText.trim()) return;
    
    const text = commentText.trim();
    
    // Optimistic update
    const newComment = {
      id: `temp-${Date.now()}`,
      text: text,
      author: 'User',
      createdAt: new Date().toISOString()
    };
    
    const updatedItems = items.map(i => {
      if (i.id === item.id) {
        return {
          ...i,
          comments: {
            ...i.comments,
            internal: [...(i.comments?.internal || []), newComment]
          }
        };
      }
      return i;
    });
    setItems(updatedItems);
    setCommentText('');
    
    try {
      // Comments not yet implemented in API
      // TODO: Implement when API is ready
      console.log('Would add comment:', text);
      fetchDiligenceTracker();
    } catch (error) {
      console.error('Error adding comment:', error);
      setError('Failed to add comment');
      // Rollback
      fetchDiligenceTracker();
    }
  };

  const handleExportCSV = async () => {
    if (!deal) return;
    
    try {
      // Export not yet implemented in API
      console.log('Would export CSV for deal:', deal.name);
      alert('CSV export coming soon!');
    } catch (error) {
      console.error('Error exporting CSV:', error);
    }
  };

  const handleExportPDF = async () => {
    if (!deal) return;
    
    try {
      // Export not yet implemented in API
      console.log('Would export PDF for deal:', deal.name);
      alert('PDF export coming soon!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
    }
  };

  const openLinkModal = (item) => {
    setLinkingItem(item);
    setShowLinkModal(true);
  };

  const handleLinkDocument = async (documentId) => {
    if (!deal || !linkingItem) return;
    
    try {
      await diligenceApi.addDocument(linkingItem.id, {
        documentId
      });
      
      fetchDiligenceTracker();
      setShowLinkModal(false);
      setLinkingItem(null);
    } catch (error) {
      console.error('Error linking document:', error);
    }
  };

  const handleUnlinkDocument = async (itemId, documentId) => {
    if (!deal) return;
    
    const docName = getDocumentName(documentId);
    if (!window.confirm(`Remove "${docName}" from this item?`)) {
      return;
    }
    
    try {
      await diligenceApi.removeDocument(itemId, documentId);
      await fetchDiligenceTracker();
    } catch (error) {
      console.error('Error unlinking document:', error);
      setError('Failed to remove document link');
    }
  };

  const handleDeleteItem = async (item, e) => {
    e.stopPropagation();
    if (!deal) return;
    if (!item.isCustom) return;
    
    if (!window.confirm(`Delete "${item.userTitle || item.title}"?`)) return;
    
    try {
      await diligenceApi.delete(item.id);
      fetchDiligenceTracker();
    } catch (error) {
      console.error('Error deleting item:', error);
      setError('Failed to delete item');
    }
  };

  // Category handlers
  const generateCodePrefix = (name) => {
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].substring(0, 3).toUpperCase();
    }
    return words.slice(0, 3).map(w => w[0]).join('').toUpperCase();
  };

  const handleCreateCategory = async () => {
    if (!deal) return;
    if (!addCategoryForm.name.trim()) return;
    
    const codePrefix = addCategoryForm.codePrefix.trim() || generateCodePrefix(addCategoryForm.name);
    
    try {
      await diligenceApi.create({
        deal_id: deal.id,
        category: 'General',
        title: addCategoryForm.name.trim(),
        description: `Category: ${addCategoryForm.name.trim()}`,
      });
      
      // Reset form and refresh
      setAddCategoryForm({ name: '', codePrefix: '', color: '#6b7280' });
      setShowAddCategory(false);
      fetchDiligenceTracker();
    } catch (error) {
      console.error('Error creating category:', error);
      setError(error.response?.data?.error || 'Failed to create category');
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!deal) return;
    if (!category.isCustom) {
      setError('Cannot delete default categories');
      return;
    }
    
    const itemsInCategory = items.filter(i => i.category === category.name);
    if (itemsInCategory.length > 0) {
      if (!window.confirm(`Delete "${category.name}"? This category has ${itemsInCategory.length} item(s) that will also be deleted.`)) {
        return;
      }
    } else {
      if (!window.confirm(`Delete "${category.name}"?`)) {
        return;
      }
    }
    
    try {
      // Categories not yet implemented in API
      fetchDiligenceTracker();
    } catch (error) {
      console.error('Error deleting category:', error);
      setError(error.response?.data?.error || 'Failed to delete category');
    }
  };

  const getCategoryColor = (category) => {
    return category.color || DEFAULT_CATEGORY_COLORS[category.name] || '#6b7280';
  };

  const getCategoryItems = (category) => {
    return items.filter(item => item.category === category);
  };

  const getCategoryStats = (category) => {
    return stats?.byCategory?.[category] || { total: 0, closed: 0, completionPct: 0 };
  };

  const getDocumentName = (docId) => {
    const doc = documents.find(d => d.jobId === docId);
    return doc?.filename || docId;
  };

  const getDocumentStatus = (docId) => {
    const doc = documents.find(d => d.jobId === docId);
    return doc?.parseStatus?.status;
  };

  // Get display title/description (user override or default)
  const getDisplayTitle = (item) => item.userTitle || item.title;
  const getDisplayDescription = (item) => item.userDescription || item.description;

  if (loading) {
    return (
      <div className="dt-container">
        <div className="dt-loading">
          <div className="dt-spinner"></div>
          <p>Loading information tracker...</p>
        </div>
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="dt-container">
        <div className="dt-empty">
          <FileSearch size={48} />
          <h3>No deal selected</h3>
          <p>Select a deal to view its information tracker</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dt-container">
      {/* Header */}
      <div className="dt-header">
        <div className="dt-title-section">
          <h1 className="dt-title">
            <FileSearch size={24} />
            Information Request Tracker
          </h1>
          <p className="dt-subtitle">{deal.name}</p>
        </div>
        
        <div className="dt-actions">
          <button 
            className="dt-btn-secondary"
            onClick={handleExportCSV}
            disabled={items.length === 0}
          >
            <Download size={16} />
            Export Excel
          </button>
          <button 
            className="dt-btn-secondary"
            onClick={handleExportPDF}
            disabled={items.length === 0}
          >
            <FileText size={16} />
            Export PDF
          </button>
        </div>
      </div>

      {/* Error/Warning Banner */}
      {error && (
        <div className={`dt-banner ${error.includes('No documents') ? 'dt-banner-warning' : 'dt-banner-error'}`}>
          <AlertTriangle size={18} />
          <span>{error}</span>
        </div>
      )}

      {/* Critical Gaps Alert */}
      {criticalGaps.length > 0 && (
        <div className="dt-critical-alert">
          <AlertTriangle size={20} />
          <div>
            <strong>Critical information gaps detected</strong>
            <p>Missing: {criticalGaps.map(g => g.title).join(', ')}</p>
          </div>
        </div>
      )}

      {/* Stats Summary */}
      {stats && (
        <div className="dt-stats">
          <div className="dt-stat-card">
            <div className="dt-stat-value" style={{ color: '#161f4a' }}>
              {stats.completionPct}%
            </div>
            <div className="dt-stat-label">Complete</div>
          </div>
          <div className="dt-stat-card">
            <div className="dt-stat-value" style={{ color: '#10b981' }}>
              {stats.closed}
            </div>
            <div className="dt-stat-label">Closed</div>
          </div>
          <div className="dt-stat-card">
            <div className="dt-stat-value" style={{ color: '#f59e0b' }}>
              {stats.partial}
            </div>
            <div className="dt-stat-label">Partial</div>
          </div>
          <div className="dt-stat-card">
            <div className="dt-stat-value" style={{ color: '#ef4444' }}>
              {stats.open}
            </div>
            <div className="dt-stat-label">Open</div>
          </div>
          <div className="dt-stat-card">
            <div className="dt-stat-value">{stats.total}</div>
            <div className="dt-stat-label">Total Items</div>
          </div>
        </div>
      )}

      {/* Add Category Button */}
      <div className="dt-add-category-header">
        {showAddCategory ? (
          <div className="dt-add-category-form">
            <input
              type="text"
              placeholder="Category Name *"
              value={addCategoryForm.name}
              onChange={(e) => {
                const name = e.target.value;
                setAddCategoryForm({
                  ...addCategoryForm,
                  name,
                  codePrefix: addCategoryForm.codePrefix || generateCodePrefix(name)
                });
              }}
              className="dt-input"
              autoFocus
            />
            <input
              type="text"
              placeholder="Code Prefix (auto)"
              value={addCategoryForm.codePrefix}
              onChange={(e) => setAddCategoryForm({...addCategoryForm, codePrefix: e.target.value})}
              className="dt-input dt-input-small"
              maxLength={5}
            />
            <div className="dt-color-picker">
              {colorOptions.map(color => (
                <button
                  key={color}
                  type="button"
                  className={`dt-color-option ${addCategoryForm.color === color ? 'active' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setAddCategoryForm({...addCategoryForm, color})}
                />
              ))}
            </div>
            <div className="dt-add-category-actions">
              <button 
                className="dt-btn-sm dt-btn-primary"
                onClick={handleCreateCategory}
                disabled={!addCategoryForm.name.trim()}
              >
                <Save size={14} /> Save
              </button>
              <button 
                className="dt-btn-sm dt-btn-secondary"
                onClick={() => {
                  setShowAddCategory(false);
                  setAddCategoryForm({ name: '', codePrefix: '', color: '#6b7280' });
                }}
              >
                <XCircle size={14} /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <button 
            className="dt-btn-add-category"
            onClick={() => setShowAddCategory(true)}
          >
            <Plus size={16} /> Add Category
          </button>
        )}
      </div>

      {/* Category Accordions */}
      <div className="dt-categories">
        {categories.map(category => {
          const categoryItems = getCategoryItems(category.name);
          const categoryStats = getCategoryStats(category.name);
          const isExpanded = expandedCategories.includes(category.name);
          
          // Always show custom categories even if empty, hide empty default categories
          if (categoryItems.length === 0 && !category.isCustom && !addingToCategory) return null;
          
          return (
            <div key={category.id} className="dt-category">
              <button 
                className="dt-category-header"
                onClick={() => toggleCategory(category.name)}
                style={{ borderLeftColor: getCategoryColor(category) }}
              >
                <div className="dt-category-title">
                  {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  <span>{category.name}</span>
                  <span className="dt-category-count">({categoryItems.length})</span>
                  {category.isCustom && (
                    <button
                      type="button"
                      className="dt-btn-delete-category"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCategory(category);
                      }}
                      title="Delete category"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
                <div className="dt-category-progress">
                  <div className="dt-progress-bar">
                    <div 
                      className="dt-progress-fill"
                      style={{ 
                        width: `${categoryStats.completionPct}%`,
                        backgroundColor: getCategoryColor(category)
                      }}
                    />
                  </div>
                  <span className="dt-progress-text">{categoryStats.completionPct}%</span>
                </div>
              </button>
              
              {isExpanded && (
                <div className="dt-category-content">
                  {/* Add Item Button */}
                  <div className="dt-add-item-header">
                    {addingToCategory === category.name ? (
                      <div className="dt-add-item-form">
                        <div className="dt-add-item-row">
                          <input
                            type="text"
                            placeholder="Code (optional)"
                            value={addItemForm.code}
                            onChange={(e) => setAddItemForm({...addItemForm, code: e.target.value})}
                            className="dt-input-code"
                          />
                          <input
                            type="text"
                            placeholder="Title *"
                            value={addItemForm.title}
                            onChange={(e) => setAddItemForm({...addItemForm, title: e.target.value})}
                            className="dt-input-title"
                          />
                        </div>
                        <textarea
                          placeholder="Description (optional)"
                          value={addItemForm.description}
                          onChange={(e) => setAddItemForm({...addItemForm, description: e.target.value})}
                          className="dt-input-description"
                          rows={2}
                        />
                        <div className="dt-add-item-row">
                          <select
                            value={addItemForm.status}
                            onChange={(e) => setAddItemForm({...addItemForm, status: e.target.value})}
                            className="dt-input-status"
                          >
                            <option value="Open">Open</option>
                            <option value="Partial">Partial</option>
                            <option value="Closed">Closed</option>
                          </select>
                          <input
                            type="text"
                            placeholder="Owner"
                            value={addItemForm.owner}
                            onChange={(e) => setAddItemForm({...addItemForm, owner: e.target.value})}
                            className="dt-input-owner"
                          />
                          <input
                            type="date"
                            value={addItemForm.dueDate}
                            onChange={(e) => setAddItemForm({...addItemForm, dueDate: e.target.value})}
                            className="dt-input-date"
                          />
                        </div>
                        <div className="dt-add-item-actions">
                          <button 
                            className="dt-btn-sm dt-btn-primary"
                            onClick={saveNewItem}
                            disabled={!addItemForm.title.trim()}
                          >
                            <Save size={14} /> Save
                          </button>
                          <button 
                            className="dt-btn-sm dt-btn-secondary"
                            onClick={cancelAddingItem}
                          >
                            <XCircle size={14} /> Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button 
                        className="dt-btn-add-item"
                        onClick={(e) => startAddingItem(category.name, e)}
                      >
                        <Plus size={14} /> Add Item
                      </button>
                    )}
                  </div>

                  {categoryItems.map(item => {
                    const statusConfig = STATUS_CONFIG[item.status];
                    const StatusIcon = statusConfig.icon;
                    const isItemExpanded = expandedItem === item.id;
                    const isEditing = editingItem === item.id;
                    
                    return (
                      <div 
                        key={item.id} 
                        className={`dt-item ${isItemExpanded ? 'dt-item-expanded' : ''}`}
                      >
                        <div 
                          className="dt-item-header"
                          onClick={() => toggleItem(item.id)}
                        >
                          <div className="dt-item-code">{item.code}</div>
                          <div className="dt-item-title-section">
                            {isEditing ? (
                              <div className="dt-edit-form" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="text"
                                  value={editForm.userTitle}
                                  onChange={(e) => setEditForm({...editForm, userTitle: e.target.value})}
                                  className="dt-edit-input"
                                  placeholder="Title"
                                  autoFocus
                                />
                                <textarea
                                  value={editForm.userDescription}
                                  onChange={(e) => setEditForm({...editForm, userDescription: e.target.value})}
                                  className="dt-edit-textarea"
                                  placeholder="Description"
                                  rows={2}
                                />
                                <div className="dt-edit-actions">
                                  <button 
                                    className="dt-btn-icon dt-btn-success"
                                    onClick={(e) => saveItemEdits(item, e)}
                                  >
                                    <Save size={14} />
                                  </button>
                                  <button 
                                    className="dt-btn-icon"
                                    onClick={cancelEditing}
                                  >
                                    <XCircle size={14} />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="dt-item-title">
                                  {getDisplayTitle(item)}
                                  <button 
                                    className="dt-btn-edit"
                                    onClick={(e) => startEditing(item, e)}
                                    title="Edit"
                                  >
                                    <Pencil size={12} />
                                  </button>
                                  {item.isCustom && (
                                    <button 
                                      className="dt-btn-delete"
                                      onClick={(e) => handleDeleteItem(item, e)}
                                      title="Delete"
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  )}
                                </div>
                                <div className="dt-item-meta">
                                  {item.linkedDocuments.length > 0 && (
                                    <span className="dt-item-doc-count">
                                      <FileText size={12} />
                                      {item.linkedDocuments.length} doc{item.linkedDocuments.length > 1 ? 's' : ''}
                                    </span>
                                  )}
                                  {item.parseError && (
                                    <span className="dt-item-parse-error">
                                      <AlertTriangle size={12} />
                                      Parse error
                                    </span>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                          <div className="dt-item-status">
                            <select
                              className="dt-status-select"
                              value={item.status}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => handleStatusChange(item, e.target.value, e)}
                              style={{ 
                                backgroundColor: statusConfig.bgColor,
                                color: statusConfig.color
                              }}
                            >
                              <option value="Open" style={{ color: '#ef4444' }}>Open</option>
                              <option value="Partial" style={{ color: '#f59e0b' }}>Partial</option>
                              <option value="Closed" style={{ color: '#10b981' }}>Closed</option>
                            </select>
                          </div>
                          {item.coverageConfidence !== null && (
                            <div className="dt-item-confidence">
                              {Math.round(item.coverageConfidence * 100)}%
                            </div>
                          )}
                        </div>
                        
                        {isItemExpanded && !isEditing && (
                          <div className="dt-item-details">
                            <p className="dt-item-description">{getDisplayDescription(item)}</p>
                            
                            {/* Linked Documents */}
                            <div className="dt-item-section">
                              <h4>Linked Documents</h4>
                              {item.linkedDocuments.length === 0 ? (
                                <p className="dt-item-empty">No documents linked</p>
                              ) : (
                                <div className="dt-linked-docs">
                                  {item.linkedDocuments.map(docId => {
                                    const parseStatus = getDocumentStatus(docId);
                                    const hasParseError = parseStatus === 'failed';
                                    
                                    return (
                                      <div key={docId} className="dt-linked-doc">
                                        <div className="dt-linked-doc-info">
                                          <FileText size={14} />
                                          <span className={hasParseError ? 'dt-parse-error' : ''}>
                                            {getDocumentName(docId)}
                                          </span>
                                          {hasParseError && (
                                            <span className="dt-parse-error-badge">
                                              <AlertTriangle size={12} />
                                              Parse failed
                                            </span>
                                          )}
                                        </div>
                                        <button 
                                          type="button"
                                          className="dt-btn-unlink"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleUnlinkDocument(item.id, docId);
                                          }}
                                          title="Remove document link"
                                        >
                                          <Trash2 size={14} />
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              <button 
                                className="dt-btn-link"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openLinkModal(item);
                                }}
                              >
                                <Plus size={14} />
                                Link Document
                              </button>
                            </div>
                            
                            {/* Parse Error Warning */}
                            {item.parseError && (
                              <div className="dt-parse-warning">
                                <AlertTriangle size={16} />
                                <span>Document could not be parsed. Re-upload OCR-enabled version.</span>
                              </div>
                            )}
                            
                            {/* Owner & Due Date */}
                            <div className="dt-item-section dt-item-fields">
                              <div className="dt-field">
                                <label><User size={14} /> Owner</label>
                                <input 
                                  type="text" 
                                  placeholder="Assign owner..."
                                  value={item.owner || ''}
                                  onChange={(e) => {
                                    diligenceApi.update(item.id, {
                                      owner: e.target.value
                                    });
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                              <div className="dt-field">
                                <label><Calendar size={14} /> Due Date</label>
                                <input 
                                  type="date"
                                  value={item.dueDate || ''}
                                  onChange={(e) => {
                                    diligenceApi.update(item.id, { due_date: e.target.value });
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                            </div>
                            
                            {/* Comments */}
                            <div className="dt-item-section">
                              <h4><MessageSquare size={14} /> Comments</h4>
                              
                              {/* Existing Comments */}
                              {item.comments?.internal?.length > 0 && (
                                <div className="dt-comments-list">
                                  {[...item.comments.internal]
                                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                                    .map(comment => (
                                      <div key={comment.id} className="dt-comment">
                                        <div className="dt-comment-header">
                                          <span className="dt-comment-author">{comment.author}</span>
                                          <span className="dt-comment-time">
                                            {new Date(comment.createdAt).toLocaleDateString()} {new Date(comment.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                          </span>
                                        </div>
                                        <p className="dt-comment-text">{comment.text}</p>
                                      </div>
                                    ))}
                                </div>
                              )}
                              
                              {/* Add Comment */}
                              <div className="dt-add-comment" onClick={(e) => e.stopPropagation()}>
                                <textarea
                                  placeholder="Add a comment..."
                                  value={commentText}
                                  onChange={(e) => setCommentText(e.target.value)}
                                  className="dt-comment-input"
                                  rows={2}
                                />
                                <button
                                  className="dt-btn-sm dt-btn-primary"
                                  onClick={() => addComment(item)}
                                  disabled={!commentText.trim()}
                                >
                                  Add Comment
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Link Document Modal */}
      {showLinkModal && linkingItem && (
        <div className="dt-modal-overlay" onClick={() => setShowLinkModal(false)}>
          <div className="dt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dt-modal-header">
              <h3>Link Document to {linkingItem.code}</h3>
              <button className="dt-modal-close" onClick={() => setShowLinkModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="dt-modal-body">
              <p>Select a document to link to <strong>{getDisplayTitle(linkingItem)}</strong></p>
              <div className="dt-doc-list">
                {documents
                  .filter(d => !linkingItem.linkedDocuments.includes(d.jobId))
                  .map(doc => (
                    <button
                      key={doc.jobId}
                      className="dt-doc-option"
                      onClick={() => handleLinkDocument(doc.jobId)}
                    >
                      <FileText size={16} />
                      <div className="dt-doc-option-info">
                        <span className="dt-doc-name">{doc.filename}</span>
                        <span className="dt-doc-type">{doc.documentType}</span>
                      </div>
                      {doc.parseStatus?.status === 'failed' && (
                        <span className="dt-doc-parse-error">Parse failed</span>
                      )}
                    </button>
                  ))}
                {documents.filter(d => !linkingItem.linkedDocuments.includes(d.jobId)).length === 0 && (
                  <p className="dt-empty-state">No available documents to link</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DiligenceTracker;
